import { ApolloError } from '@apollo/client'
import api from 'api'
import { ImpressaoAtendimentoInput } from 'api/ImpressaoAtendimentoIndividual'
import { useAlert } from 'components/alert'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { useSession } from 'components/auth/useSession'
import { useErrorHandler } from 'components/error'
import { Form, FormRenderProps } from 'components/form'
import changeValue from 'components/form/mutators/changeValue'
import { CIDADAO_PATH } from 'components/layout/SideMenu/SideMenuItems'
import { confirm } from 'components/modals/confirm'
import { useFlags } from 'config/useFlagsContext'
import { parseISO } from 'date-fns'
import { Decorator, FormApi, FormState, FormSubscription } from 'final-form'
import arrayMutators from 'final-form-arrays'
import { useApolloClient } from 'graphql/hooks'
import {
  useBundlesFhirComAssinaturaPendenteLazyQuery,
  useCancelarAtendimentoMutation,
  useCidadaoEmailUpdateMutation,
  useMarcarBundlesComoNaoEnviadoMutation,
  useMedicoesAtendimentosAnterioresQuery,
  useProblemasFaltantesLpcQuery,
  useRetificarAtendimentoMutation,
  useSalvarAtendimentoIndividualMutation,
  useSalvarAtendimentoIndividualParaAprovacaoMutation,
} from 'graphql/hooks.generated'
import {
  AcessoCbo,
  AtendimentoIndividualInput,
  BundleFhirComAssinaturaPendente,
  LembreteProfissionalIdentificadorEnum,
  TipoAtendimentoProfissional,
  TipoEstabelecimentoEnum,
} from 'graphql/types.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { useConfiguracoes } from 'hooks/configuracoes'
import { useSessionHasConselhoClasse } from 'hooks/conselho-classe/useSessionHasConselhoClasse'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { reportFormatDate, useHandleImpressao } from 'hooks/impressao/useHandleImpressao'
import {
  LembreteProfissionalSearchBy,
  useLembreteProfissional,
} from 'hooks/lembrete-profissional/useLembreteProfissional'
import { useAlertasSdc } from 'hooks/sdc/useAlertasSdc'
import { useServerTime } from 'hooks/useServerTime'
import { Dispatch, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router'
import { useImpressaoComprovanteAgendamento } from 'view/agenda/components/impressao/useImpressaoAgendamentos'
import { podeFinalizarAtendimentoObservacao } from 'view/atendimentos/atendimento-individual/atendimento-observacao/util-atendObservacao'
import {
  CidadaoCalculator,
  createAtendimentoIndividualCalculator,
  PermissionsCalculator,
} from 'view/atendimentos/atendimento-individual/calculator-atendimentoIndividual'
import { convertAtendimentoIndividualModelToInput } from 'view/atendimentos/atendimento-individual/converter'
import { logEventCancelarAtendimento } from 'view/atendimentos/atendimento-individual/logCancelarAtendimento'
import { logEventFinalizarAtendimento } from 'view/atendimentos/atendimento-individual/logFinalizarAtendimento'
import { CiapCidPreNatal, meta, SoapState, TipoServicoModel } from 'view/atendimentos/atendimento-individual/model'
import { atendimentoIndividualValidator } from 'view/atendimentos/atendimento-individual/validator-atendimentoIndividual'
import { validateCpfCns } from 'view/atendimentos/atendimento-vacinacao/validator-atendimentoVacinacao'
import { ProcedimentoAutomatico, RegistroAvaliacaoPuericultura } from 'view/atendimentos/model-atendimento'
import { AgendamentoDia } from 'view/atendimentos/types/AgendamentoDia'
import { AtendimentoProfissional } from 'view/atendimentos/types/AtendimentoProfissionalModel'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'
import { convertAtendimentoIndividualInputToRetificacaoAtendimentoInput } from 'view/retificacao-atendimento/converter-retificacaoAtendimento'
import { tipoAtendimentoRetificacaoRecord } from 'view/retificacao-atendimento/model-retificacaoAtendimento'
import { retificacaoAtendimentoConfirmModal } from 'view/retificacao-atendimento/retificacaoAtendimentoConfirmModal'
import { buildSdcValues } from 'view/sdc/utils-sdc'

import { useVerificarAgendamentosConflitantes } from '../../../agenda/hooks/useVerificarAgendamentosConflitantes'
import { atendimentoFooterMessage } from '../atendimentoFooterMessage'
import { AtendimentoIndividualFooter } from '../AtendimentoIndividualFooter'
import { PeriodoGestacaoModel } from '../components/modals/types/PeriodoGestacaoModel'
import { useAtendimentoCallbackUrl } from '../hooks/useAtendimentoCallbackUrl'
import { useAtendimentoHandleRetificar } from '../hooks/useAtendimentoHandleRetificar'
import { createVacinacaoErrorModal } from '../vacinacao/VacinacaoCalendarioView'
import { Problema } from './aside/types/ProblemaModel'
import { hasProblemaComCiapW78Ativo } from './avaliacao/components/problemas-condicoes/utils/verifications-preNatal'
import { dirtyFieldsModalConfig } from './fields-summary-modal/soapFieldModalConfigs'
import { soapFieldPaths, soapFormLabels } from './fields-summary-modal/soapFormLabels'
import { handleBackendErrorsSoap, showValidationErrorsSoapModal } from './fields-summary-modal/validation-errors-modal'
import { convertAgendarConsultasToAgendamentosConflitantesInput } from './finalizacao/components/agendar-consulta/converter-agendarConsultaAtendimento'
import { FormAtivoObjetivoEnum } from './objetivo/model'
import { grupoCboProcedimentosAutomaticos } from './plano/acessos'
import { HasAccessProcedimentos } from './plano/calculator-plano-medicoes'
import { AssinadorRepmModal } from './plano/prescricao-medicamento/modal/assinador-repm/AssinadorRepmModal'
import { SoapPage } from './SoapPage'
import { SoapRootView } from './SoapRootView'
import { handleKeyDown, transformResultSoap } from './util-soap'

const subscription: FormSubscription = {} /* evita problemas de performance */

const createDecorators = (
  apollo,
  procedimentosAutomaticos: ReadonlyArray<ProcedimentoAutomatico>,
  registroAvaliacaoPuericultura: RegistroAvaliacaoPuericultura,
  cbo2002: string,
  tipoAtendimentoProfissional: TipoAtendimentoProfissional,
  prontuarioId: ID,
  ciapCidPreNatal: CiapCidPreNatal,
  dataAtendimento: Instant,
  cidadao: CidadaoCalculator,
  tipoEstabelecimento: TipoEstabelecimentoEnum,
  permissions: PermissionsCalculator,
  ultimaDumPreNatalAtivo: LocalDate,
  isRegistroTardio: boolean,
  isObservacaoAndResponsavel: boolean,
  hasProblemaComCiapW78AtivoPersistido: boolean,
  isObservacaoAndPreNatal: boolean
): Decorator[] => [
  createAtendimentoIndividualCalculator({
    apollo,
    procedimentosAutomaticos,
    registroAvaliacaoPuericultura,
    cbo2002,
    tipoAtendimentoProfissional,
    prontuarioId,
    ciapCidPreNatal,
    dataAtendimento,
    cidadao,
    tipoEstabelecimento,
    permissions,
    ultimaDumPreNatalAtivo,
    isRegistroTardio,
    isObservacaoAndResponsavel,
    hasProblemaComCiapW78AtivoPersistido,
    isObservacaoAndPreNatal,
  }),
]

export interface SoapViewProps {
  atendimentoId: ID
  prontuarioId: ID
  cidadao: CidadaoAtendimento
  isRegistroTardio: boolean
  tiposServico: ReadonlyArray<TipoServicoModel>
  dataAtendimento: Instant
  procedimentosAutomaticos?: ReadonlyArray<ProcedimentoAutomatico>
  registroAvaliacaoPuericultura: RegistroAvaliacaoPuericultura
  cbo2002: string
  agendamentoAtendimentoId: ID
  concluiAgendamento: boolean
  hasEncaminhamentoExterno: boolean
  agendamentosDia: ReadonlyArray<AgendamentoDia>
  atendimentoProfissional: AtendimentoProfissional
  nomeAtendimento: string
  ciapCidPreNatal: CiapCidPreNatal
  cacheState: SoapState
  dirtyInitialValues: SoapState
  gestacoes: ReadonlyArray<PeriodoGestacaoModel>
  ultimaDumPreNatalAtivo: LocalDate
  problemasAtivosELatentes?: Problema[]
  problemasResolvidos?: Problema[]
  updateCache: Dispatch<SoapState>
  clearCache(updateState?: boolean): void
  headerRef: React.RefObject<HTMLDivElement>
  qtdTotalProcedimentos: number
  preNatalAtivoHasTipoGravidez: boolean
  hasIvcfPreenchidoEmAtendObservacaoAnterior: boolean
}

export function SoapView(props: Readonly<SoapViewProps>) {
  const { cacheState, dirtyInitialValues } = props
  const [initialValues] = useState(cacheState) // usado pra setar os valores iniciais do Form apenas na primeira vez que renderizar
  return (
    <div>
      <SoapForm {...props} dirtyInitialValues={dirtyInitialValues} initialValues={initialValues} />
    </div>
  )
}

export interface SoapFormProps extends SoapViewProps {
  initialValues: SoapState
}

const SoapForm = memo((props: SoapFormProps) => {
  const {
    atendimentoId,
    prontuarioId,
    isRegistroTardio,
    agendamentosDia,
    dataAtendimento,
    procedimentosAutomaticos,
    registroAvaliacaoPuericultura,
    initialValues,
    dirtyInitialValues,
    agendamentoAtendimentoId,
    concluiAgendamento,
    cbo2002,
    hasEncaminhamentoExterno,
    cidadao,
    atendimentoProfissional,
    updateCache,
    clearCache,
    nomeAtendimento,
    ciapCidPreNatal,
    problemasAtivosELatentes,
    problemasResolvidos,
    gestacoes,
    ultimaDumPreNatalAtivo,
    headerRef,
    qtdTotalProcedimentos,
    preNatalAtivoHasTipoGravidez,
    hasIvcfPreenchidoEmAtendObservacaoAnterior,
  } = props

  const history = useHistory()
  const [finalizar] = useSalvarAtendimentoIndividualMutation()
  const [retificar] = useRetificarAtendimentoMutation()
  const [enviarParaAprovacao] = useSalvarAtendimentoIndividualParaAprovacaoMutation()
  const [cancelar, { loading: isLoadingCancelar }] = useCancelarAtendimentoMutation()
  const [atualizarEmailCidadao] = useCidadaoEmailUpdateMutation()
  const { verificarAgendamentosConflitantes } = useVerificarAgendamentosConflitantes()
  const [
    buscarBundlesFhirComAssinaturaPendente,
    { data: { bundlesFhirComAssinaturaPendente } = {} },
  ] = useBundlesFhirComAssinaturaPendenteLazyQuery()
  const [marcarBundlesComoNaoEnviado] = useMarcarBundlesComoNaoEnviadoMutation()
  const handleRejection = useErrorHandler()
  const { data, isEstagio } = useSession()
  const apollo = useApolloClient()
  const dataNascimentoCidadao = Number(parseISO(cidadao?.dataNascimento))
  const { getServerTimeNow } = useServerTime()
  const dataReferencia = atendimentoProfissional?.iniciadoEm
  const tipoAtendimentoProfissional = atendimentoProfissional?.tipo
  const { analytics } = useFirebase()
  const flags = useFlags()
  const {
    PILOTO_DIABETES_HIPERTENSAO_LPC_ENABLED,
    PILOTO_RETIFICACAO_ATENDIMENTO_PROCEDIMENTOS_ENABLED,
    RETIFICACAO_ATENDIMENTO_INDIVIDUAL_ENABLED,
    PILOTO_RNDS_REPM_ENABLED,
    PILOTO_ASSINADOR_REPM_ENABLED,
  } = flags
  const { handleImpressaoPath, handleImpressaoError } = useHandleImpressao()
  const { hasCboAuth, tipoEstabelecimento } = useSession({ fetchPolicy: 'cache-only' })
  const { handleImpressaoComprovanteAgendamento } = useImpressaoComprovanteAgendamento()
  const [isModalAssinadorRepmOpen, setIsModalAssinadorRepmOpen] = useState<boolean>(false)

  const {
    cidadao: { isGestante },
    permissoes: { hasPermissionPreNatal, hasPermissionAcompanhamentoPreNatal },
    observacao: { isAtendimentoObservacao, isObservacaoAndResponsavel },
    tiposAtendimento: { isAtendimentoOdonto },
    statusRevisaoAtendimento,
    isRetificacao,
    atendimentoProfissional: {
      lotacao: { equipe },
    },
  } = useAtendimentoContext()
  const isMedicoOuEnfermeiro = useMemo(() => hasCboAuth([AcessoCbo.MEDICO, AcessoCbo.ENFERMEIRO]), [hasCboAuth])

  const {
    data: { problemasFaltantesLPC: problemasObrigatoriosAdicionarLPC },
  } = useProblemasFaltantesLpcQuery({
    variables: { prontuarioId: prontuarioId },
    skip:
      !PILOTO_DIABETES_HIPERTENSAO_LPC_ENABLED ||
      tipoAtendimentoProfissional === TipoAtendimentoProfissional.ATENDIMENTO_PROCEDIMENTOS ||
      !equipe?.tipoEquipe?.isExibeProblemasObrigatoriosAdicionarLPC ||
      !isMedicoOuEnfermeiro,
  })

  const cidadaoBaseUrl = `${CIDADAO_PATH}/${cidadao.id}`

  const { goToCallbackUrl } = useAtendimentoCallbackUrl({
    atendimentoId,
    cidadaoId: cidadao.id,
    onGoToCallbackUrl: () => clearCache(false),
    isRetificacao,
    isRegistroTardio,
  })
  const { acesso } = useAcessoLotacaoOrEstagio()
  const podeFinalizarObservacao = podeFinalizarAtendimentoObservacao(acesso, tipoEstabelecimento)
  const isObservacaoAndPreNatal =
    isAtendimentoObservacao &&
    initialValues.objetivo.atendimentosEspecificos.formAtivo === FormAtivoObjetivoEnum.PRE_NATAL

  const { isAtivo: isLembreteProfissionalAtivo, reagendarByData } = useLembreteProfissional({
    identificador: LembreteProfissionalIdentificadorEnum.MODAL_ASSINADOR_RNDS_SEM_VINCULO,
    searchBy: LembreteProfissionalSearchBy.USUARIO,
  })

  const hasConselhoClasse = useSessionHasConselhoClasse()

  const {
    configuracoes: { isAssinaturaDigitalEnabled },
  } = useConfiguracoes()

  const hasAccessProcedimentos: HasAccessProcedimentos = useMemo(
    () => ({
      desenvolvimentoCrianca: hasCboAuth(grupoCboProcedimentosAutomaticos.desenvolvimentoCrianca),
      crescimentoCrianca: hasCboAuth(grupoCboProcedimentosAutomaticos.crescimentoCrianca),
      glicemiaCapilar: hasCboAuth(grupoCboProcedimentosAutomaticos.glicemiaCapilar),
    }),
    [hasCboAuth]
  )

  const hasProblemaComCiapW78AtivoPersistido = hasProblemaComCiapW78Ativo(problemasAtivosELatentes)

  const decorators = useMemo(
    () =>
      createDecorators(
        apollo,
        procedimentosAutomaticos,
        registroAvaliacaoPuericultura,
        cbo2002,
        tipoAtendimentoProfissional,
        prontuarioId,
        ciapCidPreNatal,
        dataAtendimento,
        {
          dataNascimento: cidadao.dataNascimento,
          problemasAtivosELatentes,
          isGestante,
          idadeEmAnos: cidadao.idadeEmAnos,
          desejaInformarIdentidadeGenero: cidadao.desejaInformarIdentidadeGenero,
          sexo: cidadao.sexo,
        },
        tipoEstabelecimento,
        { hasAccessProcedimentos, hasPermissionPreNatal, hasPermissionAcompanhamentoPreNatal },
        ultimaDumPreNatalAtivo,
        isRegistroTardio,
        isObservacaoAndResponsavel,
        hasProblemaComCiapW78AtivoPersistido,
        isObservacaoAndPreNatal
      ),
    [
      apollo,
      procedimentosAutomaticos,
      registroAvaliacaoPuericultura,
      cbo2002,
      tipoAtendimentoProfissional,
      prontuarioId,
      ciapCidPreNatal,
      dataAtendimento,
      cidadao.dataNascimento,
      cidadao.idadeEmAnos,
      cidadao.desejaInformarIdentidadeGenero,
      cidadao.sexo,
      problemasAtivosELatentes,
      isGestante,
      tipoEstabelecimento,
      hasAccessProcedimentos,
      hasPermissionPreNatal,
      hasPermissionAcompanhamentoPreNatal,
      ultimaDumPreNatalAtivo,
      isRegistroTardio,
      isObservacaoAndResponsavel,
      hasProblemaComCiapW78AtivoPersistido,
      isObservacaoAndPreNatal,
    ]
  )
  const validators = useMemo(
    () =>
      atendimentoIndividualValidator(
        { ...cidadao, isGestante },
        dataAtendimento,
        tipoAtendimentoProfissional,
        problemasAtivosELatentes,
        !concluiAgendamento,
        hasEncaminhamentoExterno,
        hasPermissionPreNatal,
        isAtendimentoObservacao,
        isObservacaoAndResponsavel,
        podeFinalizarObservacao,
        getServerTimeNow,
        qtdTotalProcedimentos,
        preNatalAtivoHasTipoGravidez,
        hasProblemaComCiapW78AtivoPersistido,
        hasIvcfPreenchidoEmAtendObservacaoAnterior,
        problemasObrigatoriosAdicionarLPC,
        isMedicoOuEnfermeiro
      ),
    [
      cidadao,
      isGestante,
      dataAtendimento,
      tipoAtendimentoProfissional,
      problemasAtivosELatentes,
      concluiAgendamento,
      hasEncaminhamentoExterno,
      hasPermissionPreNatal,
      isAtendimentoObservacao,
      isObservacaoAndResponsavel,
      podeFinalizarObservacao,
      getServerTimeNow,
      qtdTotalProcedimentos,
      preNatalAtivoHasTipoGravidez,
      hasProblemaComCiapW78AtivoPersistido,
      hasIvcfPreenchidoEmAtendObservacaoAnterior,
      problemasObrigatoriosAdicionarLPC,
      isMedicoOuEnfermeiro,
    ]
  )

  const alert = useAlert()

  const nomeCompletoAtendimento = `Atendimento ${nomeAtendimento}`
  const printItem = useCallback(
    (itemToPrint: ImpressaoAtendimentoInput) => {
      handleImpressaoPath({
        apiPath: api.atendimentoIndividual.imprimirAtendimentoIndividual,
        values: itemToPrint,
        file: `AtendimentoIndividual.pdf`,
        dateFormat: reportFormatDate,
      }).catch(handleImpressaoError)
    },
    [handleImpressaoError, handleImpressaoPath]
  )

  const { setSdcAtendimentoValues } = useAlertasSdc()

  const [formInitialized, setFormInitialized] = useState(false)

  const { data: dataMedicoes } = useMedicoesAtendimentosAnterioresQuery({
    variables: { prontuarioId },
    skip: isRetificacao,
  })

  useEffect(() => {
    setFormInitialized(true)

    if (cidadao.dataNascimento) {
      const hoje = getServerTimeNow()

      setSdcAtendimentoValues(
        buildSdcValues(
          initialValues,
          prontuarioId,
          atendimentoProfissional.iniciadoEm,
          isRegistroTardio,
          cidadao.dataNascimento,
          dataMedicoes,
          hoje,
          tipoAtendimentoProfissional
        )
      )
    }
  }, [
    atendimentoProfissional.iniciadoEm,
    initialValues,
    prontuarioId,
    setSdcAtendimentoValues,
    isRegistroTardio,
    cidadao.idadeEmAnos,
    cidadao.dataNascimento,
    dataMedicoes,
    getServerTimeNow,
    tipoAtendimentoProfissional,
  ])

  const handleFormChange = useCallback(
    (values: SoapState) => {
      /* Avoids the error of updating a component while rendering a different component. */
      if (formInitialized) {
        values.lastSaved = new Date()

        if (cidadao.dataNascimento) {
          const hoje = getServerTimeNow()

          setSdcAtendimentoValues(
            buildSdcValues(
              values,
              prontuarioId,
              atendimentoProfissional.iniciadoEm,
              isRegistroTardio,
              cidadao.dataNascimento,
              dataMedicoes,
              hoje,
              tipoAtendimentoProfissional
            )
          )
        }
      }
      updateCache(values)
    },
    [
      formInitialized,
      updateCache,
      cidadao.dataNascimento,
      getServerTimeNow,
      setSdcAtendimentoValues,
      prontuarioId,
      atendimentoProfissional.iniciadoEm,
      isRegistroTardio,
      dataMedicoes,
      tipoAtendimentoProfissional,
    ]
  )

  const footerMessages = useMemo(
    () =>
      atendimentoFooterMessage(
        tipoAtendimentoProfissional,
        isAtendimentoObservacao,
        isObservacaoAndResponsavel,
        isRetificacao
      ),
    [isAtendimentoObservacao, isObservacaoAndResponsavel, isRetificacao, tipoAtendimentoProfissional]
  )

  const cancelarAtendimento = useCallback(
    (isStartObservacao) => async () => {
      try {
        await cancelar({ variables: { atendimentoId } })

        logEventCancelarAtendimento(
          analytics.logEvent,
          nomeCompletoAtendimento,
          isStartObservacao,
          isAtendimentoObservacao,
          atendimentoProfissional.finalizadoEm
        )

        goToCallbackUrl()
      } catch (e) {
        return handleRejection(e as any)
      }
    },
    [
      cancelar,
      atendimentoId,
      analytics.logEvent,
      nomeCompletoAtendimento,
      isAtendimentoObservacao,
      atendimentoProfissional.finalizadoEm,
      goToCallbackUrl,
      handleRejection,
    ]
  )

  const cancelarRetificacao = useCallback(() => {
    analytics.logEvent('cancelar_RET', {
      tipo_de_atendimento: tipoAtendimentoRetificacaoRecord[tipoAtendimentoProfissional].title,
    })
    goToCallbackUrl()
  }, [analytics, goToCallbackUrl, tipoAtendimentoProfissional])

  const handleCancelar = useCallback(
    (isStartObservacao: boolean) => {
      confirm({
        title: footerMessages.confirmTitle,
        body: footerMessages.body,
        confirmLabel: 'Sim, cancelar',
        cancelLabel: 'Não, continuar aqui',
        icon: {
          name: 'questionMarkOutline',
          fill: 'normal',
        },
        onConfirm: !isRetificacao ? cancelarAtendimento(isStartObservacao) : cancelarRetificacao,
      })()
    },
    [footerMessages.confirmTitle, footerMessages.body, isRetificacao, cancelarAtendimento, cancelarRetificacao]
  )

  const handleClickAtualizarCadastroCidadao = useCallback(
    () =>
      history.push(`${cidadaoBaseUrl}/edit?callbackUrl=lista-atendimento/atendimento&callbackParams=${atendimentoId}`),
    [atendimentoId, cidadaoBaseUrl, history]
  )

  const { handleRetificar } = useAtendimentoHandleRetificar({
    tipoAtendimentoProfissional,
    printItem,
    onFinalizar: retificar,
    goToCallbackUrl,
  })

  const handleEstagio = useCallback(
    async (input: AtendimentoIndividualInput, values: SoapState) => {
      try {
        const enviarAprovacaoResponse = await enviarParaAprovacao({
          variables: {
            input: {
              atendimentoInput: input,
              rascunho: JSON.stringify(values),
            },
          },
        })

        if (enviarAprovacaoResponse.data.salvarAtendimentoIndividualParaAprovacao) {
          analytics.logEvent('atendimento_enviado_revisao_EST', { tipo_de_atendimento: nomeCompletoAtendimento })
          alert('success', `${nomeCompletoAtendimento} enviado para aprovação com sucesso.`)
          goToCallbackUrl()
        }
      } catch (error) {
        if (error instanceof ApolloError) {
          handleBackendErrorsSoap(
            error,
            isEstagio,
            flags,
            tipoAtendimentoProfissional,
            isAtendimentoOdonto,
            isRetificacao
          )
        }
      }
    },
    [
      enviarParaAprovacao,
      analytics,
      nomeCompletoAtendimento,
      alert,
      goToCallbackUrl,
      flags,
      isEstagio,
      tipoAtendimentoProfissional,
      isAtendimentoOdonto,
      isRetificacao,
    ]
  )

  const handleFinalizarRetificacao = useCallback(
    async (input: AtendimentoIndividualInput, formApi: FormApi<SoapState>) => {
      const { initialValues, values } = formApi.getState()

      const retificacaoAtendimentoInput = convertAtendimentoIndividualInputToRetificacaoAtendimentoInput(
        input,
        atendimentoProfissional.id,
        values.retificacaoAtendimento
      )

      await retificacaoAtendimentoConfirmModal({
        initialValues,
        currentValues: values,
        nomeCidadao: (cidadao.nomeSocial ?? cidadao.nome).titleCase(),
        labels: soapFormLabels,
        config: dirtyFieldsModalConfig,
        meta,
        onRetificar: () => handleRetificar(retificacaoAtendimentoInput, values.imprimirAtendimento),
        tipoAtendimentoProfissional,
        onRetificarSemAlteracoes: goToCallbackUrl,
      })
    },
    [
      atendimentoProfissional.id,
      cidadao.nomeSocial,
      cidadao.nome,
      goToCallbackUrl,
      handleRetificar,
      tipoAtendimentoProfissional,
    ]
  )
  const emailCidadao = cidadao.contato?.email
  const nomeCidadao = useMemo(() => ({ nome: cidadao.nome, nomeSocial: cidadao.nomeSocial }), [
    cidadao.nome,
    cidadao.nomeSocial,
  ])

  const handleCloseModalAssinadorRepm = useCallback(() => {
    setIsModalAssinadorRepmOpen(false)
    alert('success', `${nomeCompletoAtendimento} finalizado com sucesso.`)
    goToCallbackUrl()
    marcarBundlesComoNaoEnviado({
      variables: {
        atendProfId: atendimentoProfissional?.id,
      },
    })
  }, [alert, goToCallbackUrl, nomeCompletoAtendimento, marcarBundlesComoNaoEnviado, atendimentoProfissional?.id])

  const handleFinalizarPadrao = useCallback(
    async (input: AtendimentoIndividualInput, values: SoapState) => {
      const comprovanteAgendamento = values?.finalizacao?.agendamentoConsultas?.comprovante
      if (comprovanteAgendamento?.enviarParaCidadao) {
        const novoEmailCidadao = values.finalizacao.agendamentoConsultas.comprovante.emailCidadao
        if (novoEmailCidadao && emailCidadao !== novoEmailCidadao) {
          await atualizarEmailCidadao({
            variables: {
              input: {
                cidadaoId: cidadao.id,
                email: novoEmailCidadao,
              },
            },
          })
        }
      }

      const {
        data: { salvarAtendimentoIndividual: finalizarResponse },
      } = await finalizar({ variables: { input } })

      const atendProfId = finalizarResponse.atendProf.id

      values.imprimirAtendimento &&
        printItem({
          atendimentoProfissionalId: atendProfId,
        })

      comprovanteAgendamento?.imprimir &&
        finalizarResponse.agendamentosCriadosIds.length &&
        handleImpressaoComprovanteAgendamento(nomeCidadao, finalizarResponse.agendamentosCriadosIds)

      logEventFinalizarAtendimento(
        analytics.logEvent,
        nomeCompletoAtendimento,
        values,
        cidadao.idadeEmAnos,
        isGestante,
        initialValues.antecedentes?.pessoal,
        isAtendimentoObservacao,
        statusRevisaoAtendimento
      )

      if (PILOTO_RNDS_REPM_ENABLED && PILOTO_ASSINADOR_REPM_ENABLED) {
        const { data: { bundlesFhirComAssinaturaPendente } = {} } = await buscarBundlesFhirComAssinaturaPendente({
          variables: { atendProfId },
        })

        if (bundlesFhirComAssinaturaPendente?.isNotEmpty()) {
          const podeAbrirModalAssinador =
            isAssinaturaDigitalEnabled && hasConselhoClasse && atendProfId && isLembreteProfissionalAtivo

          if (podeAbrirModalAssinador) {
            return setIsModalAssinadorRepmOpen(true)
          }

          marcarBundlesComoNaoEnviado({
            variables: {
              atendProfId,
            },
          })
        }
      }

      alert('success', `${nomeCompletoAtendimento} finalizado com sucesso.`)
      goToCallbackUrl()
    },
    [
      finalizar,
      printItem,
      alert,
      analytics.logEvent,
      atualizarEmailCidadao,
      cidadao.id,
      cidadao.idadeEmAnos,
      emailCidadao,
      goToCallbackUrl,
      handleImpressaoComprovanteAgendamento,
      initialValues.antecedentes?.pessoal,
      isAtendimentoObservacao,
      isGestante,
      nomeCidadao,
      nomeCompletoAtendimento,
      statusRevisaoAtendimento,
      PILOTO_RNDS_REPM_ENABLED,
      PILOTO_ASSINADOR_REPM_ENABLED,
      isLembreteProfissionalAtivo,
      hasConselhoClasse,
      isAssinaturaDigitalEnabled,
      buscarBundlesFhirComAssinaturaPendente,
      marcarBundlesComoNaoEnviado,
    ]
  )

  const finalizarAtendimentoProfissional = useCallback(
    async (input: AtendimentoIndividualInput, values: SoapState, formApi: FormApi<SoapState>) => {
      try {
        if (isRetificacao) {
          await handleFinalizarRetificacao(input, formApi)
        } else {
          await handleFinalizarPadrao(input, values)
        }
      } catch (error) {
        if (error instanceof ApolloError)
          handleBackendErrorsSoap(
            error,
            isEstagio,
            flags,
            tipoAtendimentoProfissional,
            isAtendimentoOdonto,
            isRetificacao
          )
      }
    },
    [
      isRetificacao,
      handleFinalizarRetificacao,
      handleFinalizarPadrao,
      flags,
      isAtendimentoOdonto,
      isEstagio,
      tipoAtendimentoProfissional,
    ]
  )

  const handleFinalizar = useCallback(
    async (values: SoapState, formApi: FormApi<SoapState>) => {
      const errorCpfCnsRequired =
        values.vacinacao?.registroVacinacao?.length &&
        validateCpfCns(cidadao.cns || cidadao.cpf, handleClickAtualizarCadastroCidadao, false)

      if (errorCpfCnsRequired?.hasError) {
        createVacinacaoErrorModal(errorCpfCnsRequired)
        return
      }

      const confirmouHorariosConflitantes = await verificarAgendamentosConflitantes(
        cidadao.id,
        convertAgendarConsultasToAgendamentosConflitantesInput(values.finalizacao?.agendamentoConsultas),
        cidadao.nomeSocial ?? cidadao.nome
      )

      if (!confirmouHorariosConflitantes) return

      const input = convertAtendimentoIndividualModelToInput(
        atendimentoId,
        values,
        dataNascimentoCidadao,
        dataReferencia,
        tipoAtendimentoProfissional,
        isAtendimentoObservacao,
        isObservacaoAndResponsavel,
        isAtendimentoOdonto,
        flags
      )

      if (isEstagio) {
        return handleEstagio(input, values)
      }
      return finalizarAtendimentoProfissional(input, values, formApi)
    },
    [
      cidadao.cns,
      cidadao.cpf,
      cidadao.id,
      cidadao.nomeSocial,
      cidadao.nome,
      handleClickAtualizarCadastroCidadao,
      verificarAgendamentosConflitantes,
      atendimentoId,
      dataNascimentoCidadao,
      dataReferencia,
      tipoAtendimentoProfissional,
      isAtendimentoObservacao,
      isObservacaoAndResponsavel,
      isAtendimentoOdonto,
      flags,
      isEstagio,
      handleEstagio,
      finalizarAtendimentoProfissional,
    ]
  )

  const handleSubmitFailed = (formState: FormState<SoapState>) => {
    const { errors, hasValidationErrors } = formState

    if (hasValidationErrors) {
      showValidationErrorsSoapModal({
        errors,
        onClose: () => analytics.logEvent('erro_finalizar_atend', { erro_atendimento: [JSON.stringify(errors)] }),
      })
    }
  }
  const handleOnFocus = useCallback(() => {
    const activeElement = document.activeElement as HTMLElement
    if (activeElement) {
      const activeElementTop = activeElement.getBoundingClientRect().top
      activeElementTop < 0 && window.scrollTo(0, activeElement.parentElement.offsetTop - 160)
    }
  }, [])

  const renderForm = useCallback(
    (formProps: FormRenderProps<SoapState>): JSX.Element => {
      const { handleSubmit } = formProps

      return (
        <>
          <form
            noValidate
            onKeyDown={handleKeyDown}
            onFocus={handleOnFocus}
            style={{ display: 'flex', flexDirection: 'column', minHeight: '60vh' }}
          >
            <SoapPage
              atendimentoProfissional={atendimentoProfissional}
              dataAtualizacaoObstetricosAntecedentes={
                props.cacheState?.antecedentes?.pessoal?.informacoesObstetricas?.dataAtualizacao
              }
              dataAtualizacaoPuericulturaAntecedentes={
                props.cacheState?.antecedentes?.pessoal?.puericultura?.dataAtualizacao
              }
              cidadao={cidadao}
              dataAtendimento={dataAtendimento}
              prontuarioId={prontuarioId}
              ciapCidPreNatal={ciapCidPreNatal}
              acesso={data.acesso}
              agendamentoAtendimentoId={agendamentoAtendimentoId}
              concluiAgendamento={concluiAgendamento}
              agendamentosDia={agendamentosDia}
              atendimentoId={atendimentoId}
              handleFormChange={handleFormChange}
              profissionalId={data.profissional.id}
              gestacoes={gestacoes}
              problemasAtivosELatentes={problemasAtivosELatentes}
              problemasResolvidos={problemasResolvidos}
              headerRef={headerRef}
              hasProblemaComCiapW78AtivoPersistido={hasProblemaComCiapW78AtivoPersistido}
              hasIvcfPreenchidoEmAtendObservacaoAnterior={hasIvcfPreenchidoEmAtendObservacaoAnterior}
            />
            <AtendimentoIndividualFooter
              nomeAtendimento={nomeAtendimento}
              handleSubmit={handleSubmit}
              handleCancel={handleCancelar}
              cancelButtonLabel={footerMessages.labelButton}
              isLoadingCancelar={isLoadingCancelar}
            />
          </form>
          <SoapRootView
            atendimentoProfissional={atendimentoProfissional}
            cidadao={cidadao}
            dataAtendimento={dataAtendimento}
            isAtendimentoOdonto={isAtendimentoOdonto}
            prontuarioId={prontuarioId}
            ciapCidPreNatal={ciapCidPreNatal}
            isAtendimentoObservacao={isAtendimentoObservacao}
          />
          {isModalAssinadorRepmOpen && (
            <AssinadorRepmModal
              open={isModalAssinadorRepmOpen}
              onClose={handleCloseModalAssinadorRepm}
              bundlesFhirComAssinaturaPendente={bundlesFhirComAssinaturaPendente as BundleFhirComAssinaturaPendente[]}
              atendimentoId={atendimentoId}
              reagendarLembrete={reagendarByData}
            />
          )}
        </>
      )
    },
    [
      handleOnFocus,
      atendimentoProfissional,
      props.cacheState?.antecedentes?.pessoal?.informacoesObstetricas?.dataAtualizacao,
      props.cacheState?.antecedentes?.pessoal?.puericultura?.dataAtualizacao,
      cidadao,
      dataAtendimento,
      prontuarioId,
      ciapCidPreNatal,
      data.acesso,
      data.profissional.id,
      agendamentoAtendimentoId,
      concluiAgendamento,
      agendamentosDia,
      atendimentoId,
      handleFormChange,
      gestacoes,
      problemasAtivosELatentes,
      problemasResolvidos,
      headerRef,
      hasProblemaComCiapW78AtivoPersistido,
      hasIvcfPreenchidoEmAtendObservacaoAnterior,
      nomeAtendimento,
      handleCancelar,
      footerMessages.labelButton,
      isLoadingCancelar,
      isAtendimentoOdonto,
      isAtendimentoObservacao,
      isModalAssinadorRepmOpen,
      handleCloseModalAssinadorRepm,
      reagendarByData,
      bundlesFhirComAssinaturaPendente,
    ]
  )

  const initialDirtyFields = useMemo(
    () =>
      PILOTO_RETIFICACAO_ATENDIMENTO_PROCEDIMENTOS_ENABLED || RETIFICACAO_ATENDIMENTO_INDIVIDUAL_ENABLED
        ? { values: dirtyInitialValues, names: soapFieldPaths }
        : null,
    [
      dirtyInitialValues,
      PILOTO_RETIFICACAO_ATENDIMENTO_PROCEDIMENTOS_ENABLED,
      RETIFICACAO_ATENDIMENTO_INDIVIDUAL_ENABLED,
    ]
  )

  return (
    <Form<SoapState>
      render={renderForm}
      initialValues={initialValues}
      initialDirtyFields={initialDirtyFields}
      decorators={decorators}
      validateOnBlur
      validate={validators}
      subscription={subscription}
      onSubmit={handleFinalizar}
      onSubmitFailed={handleSubmitFailed}
      transformResult={(...args) => transformResultSoap(...args, isRetificacao)}
      mutators={{
        ...arrayMutators,
        changeValue,
      }}
    />
  )
})
